import { ChevronLeftIcon, ChevronRightIcon, ClockIcon } from "@heroicons/react/20/solid";
import { FilmIcon } from "@heroicons/react/24/outline";
import { getVideoSceneFrameUrl } from "@/api/renderer/api";
import { VideoInsightScene } from "@/api/studio/insights/types/attention.type";
import { RendererImage } from "@/components/ui/renderer-image";
import { VideoCopilotScenePanel } from "@/features/reports/components/in-depth/video-copilot-scene-panel";
import { VideoMetricRow } from "@/features/reports/components/video-metric-row";
import { asPlaybackTime } from "@/utils/format";
import { cn } from "@/utils/classes";

/* Prop Types */
interface VideoSceneDetailProps {
  projectId: string;
  studyId: string;
  videoKey: string;
  scenes: VideoInsightScene[];
  selectedSceneKey: string | null;
  onSelectScene: (sceneKey: string) => void;
}

/**
 * VideoSceneDetail component
 *
 * Shows the currently selected scene's thumbnail, time range, duration, description, and its
 * Attention / Emotion / Memory quality scores, with prev/next navigation between scenes. Renders a
 * placeholder until a scene is selected.
 */
export const VideoSceneDetail = ({ projectId, studyId, videoKey, scenes, selectedSceneKey, onSelectScene }: VideoSceneDetailProps) => {
  const selectedIndex = scenes.findIndex((scene) => scene.scene === selectedSceneKey);
  const selectedScene = selectedIndex === -1 ? undefined : scenes[selectedIndex];

  if (!selectedScene) {
    return (
      <div className="flex items-center justify-center gap-2 bg-[#242c35] rounded-2xl p-4 h-[200px]">
        <FilmIcon className="size-5 text-[#9ca3af]" />
        <p className="text-[#9ca3af] font-medium">Select a scene to reveal scores</p>
      </div>
    );
  }

  /* Consts */
  const { scores } = selectedScene;
  // Hook scores the first 3s and Hold the rest, so a scene only ever has one of them; a missing
  // Hook means the scene is later in the video, anything else missing hasn't been scored yet
  const pillars = [
    {
      label: "Attention",
      qualityScores: [
        { label: "Hook", score: scores.hook, nullLabel: "Earlier" },
        { label: "Hold", score: scores.hold },
        { label: "Visual ease", score: scores.visualEase },
      ],
    },
    { label: "Emotion", qualityScores: [{ label: "Emotion intensity", score: scores.intensity }] },
    { label: "Memory", qualityScores: [{ label: "Brand attribution", score: scores.brandAttribution }] },
  ];

  /* Render */
  return (
    <div className="flex flex-col gap-3 bg-[#242c35] rounded-2xl p-4 min-w-0">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <p className="text-white text-sm font-semibold">Scene {selectedIndex + 1}</p>
        <div className="flex-1" />
        <span className="text-[#9ca3af] text-xs font-semibold">
          {selectedIndex + 1} of {scenes.length}
        </span>
        <button
          type="button"
          onClick={() => onSelectScene(scenes[selectedIndex - 1].scene)}
          disabled={selectedIndex === 0}
          aria-label="Previous scene"
          className={cn("flex items-center justify-center size-8 rounded-lg", selectedIndex === 0 ? "opacity-30" : "cursor-pointer hover:bg-[#323c49]")}
        >
          <ChevronLeftIcon className="size-4 text-white" />
        </button>
        <button
          type="button"
          onClick={() => onSelectScene(scenes[selectedIndex + 1].scene)}
          disabled={selectedIndex === scenes.length - 1}
          aria-label="Next scene"
          className={cn("flex items-center justify-center size-8 rounded-lg", selectedIndex === scenes.length - 1 ? "opacity-30" : "cursor-pointer hover:bg-[#323c49]")}
        >
          <ChevronRightIcon className="size-4 text-white" />
        </button>
      </div>

      {/* Thumbnail, time range, duration, description, and scores */}
      <div className="flex gap-6 items-start">
        <div className="w-70 h-full shrink-0 rounded-lg overflow-hidden relative bg-[#323c49]">
          {selectedScene.frames[0] && (
            <RendererImage
              src={getVideoSceneFrameUrl({ projectId, studyId, assetKey: videoKey, frameName: selectedScene.frames[0] })}
              alt={`Scene ${selectedIndex + 1}`}
              className="absolute inset-0"
              fit="contain"
            />
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1">
              <FilmIcon className="size-4 text-white" />
              <span className="text-white text-sm font-semibold">
                {asPlaybackTime(selectedScene.startTime)} - {asPlaybackTime(selectedScene.endTime)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <ClockIcon className="size-4 text-white" />
              <span className="text-white text-sm font-semibold">{selectedScene.duration.toFixed(2)}s</span>
            </div>
          </div>
          <p className="text-white text-sm pt-4">{selectedScene.description.summary}</p>
        </div>

        <div className="w-[380px] shrink-0 flex flex-col gap-px rounded-lg overflow-hidden">
          {pillars.map(({ label, qualityScores }) => (
            <section key={label} aria-label={label} className="flex flex-col gap-2 bg-[#323c49] px-4 py-3 text-white">
              <h3 className="text-xs font-medium text-[#9ca3af]">{label}</h3>
              {qualityScores.map((qualityScore) => (
                <VideoMetricRow
                  key={qualityScore.label}
                  label={qualityScore.label}
                  score={qualityScore.score}
                  nullLabel={qualityScore.nullLabel}
                  labelClassName="text-base-content"
                />
              ))}
            </section>
          ))}
        </div>
      </div>

      {selectedScene.summary && <VideoCopilotScenePanel analysis={selectedScene.summary} />}
    </div>
  );
};
